import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type CallStatus = "idle" | "calling" | "ringing" | "connected" | "ended";

interface UseWebRTCOptions {
  chatId: string;
  currentUserId: string;
  isVideo: boolean;
  onIncomingCall?: (fromUserId: string, isVideo: boolean) => void;
}

const STUN_ONLY_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun2.l.google.com:19302" },
  { urls: "stun:stun3.l.google.com:19302" },
];

const testTurnServer = async (server: RTCIceServer, timeoutMs = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    const pc = new RTCPeerConnection({ iceServers: [server] });
    let found = false;
    const timer = setTimeout(() => {
      pc.close();
      resolve(false);
    }, timeoutMs);

    pc.onicecandidate = (e) => {
      if (e.candidate && e.candidate.type === "relay") {
        found = true;
        clearTimeout(timer);
        pc.close();
        resolve(true);
      }
    };
    pc.onicegatheringstatechange = () => {
      if (pc.iceGatheringState === "complete" && !found) {
        clearTimeout(timer);
        pc.close();
        resolve(false);
      }
    };

    // Create a dummy data channel to trigger ICE gathering
    pc.createDataChannel("test");
    pc.createOffer().then((offer) => pc.setLocalDescription(offer)).catch(() => {
      clearTimeout(timer);
      pc.close();
      resolve(false);
    });
  });
};

const fetchTurnCredentials = async (): Promise<RTCIceServer[]> => {
  try {
    const { data, error } = await supabase.functions.invoke("get-turn-credentials");
    if (error || !data?.iceServers) {
      console.warn("Failed to fetch TURN credentials, using STUN only:", error);
      return STUN_ONLY_SERVERS;
    }

    const allServers: RTCIceServer[] = [...STUN_ONLY_SERVERS, ...data.iceServers];

    // Test first TURN server for connectivity
    const turnServers = data.iceServers.filter((s: RTCIceServer) => {
      const url = Array.isArray(s.urls) ? s.urls[0] : s.urls;
      return url?.startsWith("turn:");
    });

    if (turnServers.length > 0) {
      console.log("Testing TURN server connectivity...");
      const turnWorks = await testTurnServer(turnServers[0], 5000);
      if (turnWorks) {
        console.log("✅ TURN server reachable, using full ICE config");
        return allServers;
      } else {
        console.warn("⚠️ TURN server unreachable, falling back to STUN only");
        return STUN_ONLY_SERVERS;
      }
    }

    return allServers;
  } catch (e) {
    console.warn("TURN fetch error, using STUN only:", e);
    return STUN_ONLY_SERVERS;
  }
};

export const useWebRTC = ({ chatId, currentUserId, isVideo }: UseWebRTCOptions) => {
  const [status, setStatus] = useState<CallStatus>("idle");
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(!isVideo);
  const [isVideoEnabled, setIsVideoEnabled] = useState(isVideo);
  const [isSpeaker, setIsSpeaker] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    remoteStreamRef.current = null;
    setCallDuration(0);
  }, []);

  const setupPeerConnection = useCallback(async () => {
    const iceServers = await fetchTurnCredentials();
    const pc = new RTCPeerConnection({ iceServers });
    pcRef.current = pc;

    const remoteStream = new MediaStream();
    remoteStreamRef.current = remoteStream;

    pc.ontrack = (event) => {
      event.streams[0].getTracks().forEach(track => {
        remoteStream.addTrack(track);
      });
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
      }
      // Also set audio element for speakerphone control
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && channelRef.current) {
        channelRef.current.send({
          type: "broadcast",
          event: "ice-candidate",
          payload: { candidate: event.candidate.toJSON(), from: currentUserId },
        });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") {
        setStatus("connected");
        timerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
      } else if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
        setStatus("ended");
        cleanup();
      }
    };

    return pc;
  }, [currentUserId, cleanup]);

  const getMedia = useCallback(async (video: boolean) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: video ? { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } } : false,
    });
    localStreamRef.current = stream;
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
    }
    return stream;
  }, []);

  const setupSignaling = useCallback(() => {
    const channel = supabase.channel(`call-${chatId}`);
    channelRef.current = channel;

    channel
      .on("broadcast", { event: "offer" }, async (msg) => {
        if (msg.payload?.from === currentUserId) return;
        const pc = pcRef.current;
        if (!pc) return;
        await pc.setRemoteDescription(new RTCSessionDescription(msg.payload.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        channel.send({
          type: "broadcast",
          event: "answer",
          payload: { sdp: answer, from: currentUserId },
        });
      })
      .on("broadcast", { event: "answer" }, async (msg) => {
        if (msg.payload?.from === currentUserId) return;
        const pc = pcRef.current;
        if (!pc) return;
        await pc.setRemoteDescription(new RTCSessionDescription(msg.payload.sdp));
      })
      .on("broadcast", { event: "ice-candidate" }, async (msg) => {
        if (msg.payload?.from === currentUserId) return;
        const pc = pcRef.current;
        if (!pc) return;
        try {
          await pc.addIceCandidate(new RTCIceCandidate(msg.payload.candidate));
        } catch (e) {
          console.warn("ICE candidate error:", e);
        }
      })
      .on("broadcast", { event: "end-call" }, () => {
        setStatus("ended");
        cleanup();
      })
      .subscribe();

    return channel;
  }, [chatId, currentUserId, cleanup]);

  const startCall = useCallback(async (videoCall: boolean) => {
    try {
      setStatus("calling");
      const stream = await getMedia(videoCall);
      const pc = await setupPeerConnection();
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      const channel = setupSignaling();

      channel.send({
        type: "broadcast",
        event: "incoming-call",
        payload: { from: currentUserId, isVideo: videoCall, chatId },
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      channel.send({
        type: "broadcast",
        event: "offer",
        payload: { sdp: offer, from: currentUserId },
      });
    } catch (err) {
      console.error("Error starting call:", err);
      setStatus("idle");
      cleanup();
    }
  }, [currentUserId, chatId, getMedia, setupPeerConnection, setupSignaling, cleanup]);

  const answerCall = useCallback(async (videoCall: boolean) => {
    try {
      setStatus("connected");
      const stream = await getMedia(videoCall);
      const pc = await setupPeerConnection();
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      setupSignaling();
    } catch (err) {
      console.error("Error answering call:", err);
      setStatus("idle");
      cleanup();
    }
  }, [getMedia, setupPeerConnection, setupSignaling, cleanup]);

  const endCall = useCallback(() => {
    if (channelRef.current) {
      channelRef.current.send({
        type: "broadcast",
        event: "end-call",
        payload: { from: currentUserId },
      });
    }
    setStatus("ended");
    cleanup();
  }, [currentUserId, cleanup]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (stream) {
      stream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsMuted(prev => !prev);
    }
  }, []);

  const toggleCamera = useCallback(async () => {
    const stream = localStreamRef.current;
    const pc = pcRef.current;
    if (!stream || !pc) return;

    const existingVideoTracks = stream.getVideoTracks();

    if (existingVideoTracks.length > 0) {
      // Toggle existing video tracks
      existingVideoTracks.forEach(t => { t.enabled = !t.enabled; });
      setIsCameraOff(prev => !prev);
    } else {
      // No video track yet — add one (upgrading from audio to video)
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        });
        const videoTrack = videoStream.getVideoTracks()[0];
        stream.addTrack(videoTrack);
        pc.addTrack(videoTrack, stream);

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        setIsCameraOff(false);
        setIsVideoEnabled(true);
      } catch (e) {
        console.warn("Cannot add video track:", e);
      }
    }
  }, []);

  const toggleSpeaker = useCallback(() => {
    setIsSpeaker(prev => {
      const next = !prev;
      const audioEl = remoteAudioRef.current || remoteVideoRef.current;
      if (audioEl) {
        audioEl.volume = next ? 1.0 : 0.15;
      }
      return next;
    });
  }, []);

  const flipCamera = useCallback(async () => {
    const stream = localStreamRef.current;
    const pc = pcRef.current;
    if (!stream || !pc) return;

    const oldVideoTracks = stream.getVideoTracks();
    if (oldVideoTracks.length === 0) return;

    const newFacing = facingMode === "user" ? "environment" : "user";

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacing, width: { ideal: 640 }, height: { ideal: 480 } },
      });
      const newTrack = newStream.getVideoTracks()[0];

      // Replace track in peer connection
      const sender = pc.getSenders().find(s => s.track?.kind === "video");
      if (sender) {
        await sender.replaceTrack(newTrack);
      }

      // Replace track in local stream
      oldVideoTracks.forEach(t => {
        stream.removeTrack(t);
        t.stop();
      });
      stream.addTrack(newTrack);

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      setFacingMode(newFacing);
    } catch (e) {
      console.warn("Cannot flip camera:", e);
    }
  }, [facingMode]);

  useEffect(() => {
    return () => { cleanup(); };
  }, [cleanup]);

  return {
    status,
    isMuted,
    isCameraOff,
    isVideoEnabled,
    isSpeaker,
    callDuration,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    startCall,
    answerCall,
    endCall,
    toggleMute,
    toggleCamera,
    toggleSpeaker,
    flipCamera,
    facingMode,
  };
};
